import React from 'react';
import {
  ArrowLeft,
  Building2,
  Check,
  ChevronRight,
  Info,
  MapPin,
  Phone,
  Plus,
  Printer,
  QrCode,
  TriangleAlert,
  UserRound,
} from 'lucide-react';
import {
  POSTER_THEMES,
  POSTER_TOTAL,
  findPoster,
  type PosterTheme,
  type PosterThemeKey,
  type PosterVariant,
} from '../data/posterThemes';
import {
  A4_HEIGHT_PX,
  A4_WIDTH_PX,
  PosterScaler,
  PosterSheet,
  PosterSheetStyles,
  useFitScale,
  type PosterQr,
} from '../components/PosterSheet';
import type { QRPoint } from '../types';
import { posterIdFromPath } from '../routes';
import { logPosterPrint } from '../services/supabase';

/* 安全掲示ポスター ギャラリー。
 *
 * 想定利用者は現場の安全担当者。PCに不慣れな層も含むので、
 * 画面は2つだけにする（一覧 → プレビュー）。ウィザードにはしない。
 *  - 一覧では「紙の形」で見せる。掲示物であることは判型が一番速く伝える。
 *  - 標語は紙面の縮小では読めないので、カードの説明文に実テキストで併記する。
 *  - 「選ぶ → 入れる → 刷る」の3手順を常時見せ、今どこにいるかを分からせる。
 *  - 選択は URL（/posters/H1）に残す。戻るボタンでサイトから出てしまわないように。
 */

const STORAGE_KEY = 'anzenpost_poster_publisher';
const THUMB_MIN_WIDTH = 232;
const THUMB_GAP = 20;

interface PosterGalleryViewProps {
  isSignedIn: boolean;
  qrPoints: readonly QRPoint[];
  defaultOrgName?: string;
  /** 最初に選んでおくQRポイント。
   *
   *  QR管理から「この現場のポスターを刷る」導線で入ってきたときに指定する。
   *  現場ごとにQRを分けている運用では、選び直させると
   *  「別の現場のQRを刷ってしまう」事故が起きうるので、選択済みで開く。 */
  initialQrPointId?: string;
  /** 最初に開いておくテーマ。未指定なら熱中症（公開ページの既定）。 */
  initialThemeKey?: PosterThemeKey;
  /** 未ログイン時の登録導線。 */
  onSignup: () => void;
  /** QRポイントが無いときの作成導線。 */
  onManageQr: () => void;
  /** ページ内の戻り導線。サイドバーのある管理画面の中で表示するときは省略する
   *  （サイドバーと二重になり、どちらが戻り先か分からなくなる）。 */
  onGoHome?: () => void;
  /** onGoHome の表示名。管理画面から来たときは戻り先が違う。 */
  homeLabel?: string;
}

/** 親要素の実幅を返す（サムネイルの段組み計算用）。
 *  callback ref にする理由は useFitScale と同じ（後から現れる要素を計測できるように）。 */
function useElementWidth() {
  const [el, setEl] = React.useState<HTMLDivElement | null>(null);
  const [width, setWidth] = React.useState(0);
  React.useEffect(() => {
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return { ref: setEl, width };
}

const StepRail: React.FC<{ current: 1 | 2 }> = ({ current }) => {
  const steps = [
    { n: 1, label: 'デザインを選ぶ' },
    { n: 2, label: '会社名・連絡先を入れる' },
    { n: 3, label: 'A4で印刷して掲示' },
  ];
  // 2と3は同じ画面なので、プレビューに入ったら両方を「いまここ」として扱う。
  const isActive = (n: number) => (current === 1 ? n === 1 : n >= 2);
  const isDone = (n: number) => current === 2 && n === 1;

  return (
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-2 text-sm">
      {steps.map((s, i) => (
        <li key={s.n} className="flex items-center gap-2">
          <span
            className={`flex items-center gap-2 rounded-full border px-3 py-1.5 font-bold ${
              isActive(s.n)
                ? 'border-safety-500 bg-safety-50 text-safety-700'
                : isDone(s.n)
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-slate-200 bg-white text-slate-400'
            }`}
          >
            <span
              className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${
                isActive(s.n)
                  ? 'bg-safety-500 text-white'
                  : isDone(s.n)
                    ? 'bg-emerald-500 text-white'
                    : 'bg-slate-200 text-slate-500'
              }`}
            >
              {isDone(s.n) ? <Check size={12} strokeWidth={3} /> : s.n}
            </span>
            {s.label}
          </span>
          {i < steps.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
        </li>
      ))}
    </ol>
  );
};

export const PosterGalleryView: React.FC<PosterGalleryViewProps> = ({
  isSignedIn,
  qrPoints,
  defaultOrgName,
  initialQrPointId,
  initialThemeKey,
  onSignup,
  onManageQr,
  onGoHome,
  homeLabel = 'トップへ',
}) => {
  // ── 掲示者情報。刷り直しのたびに打ち直させない。
  const [orgName, setOrgName] = React.useState('');
  const [siteName, setSiteName] = React.useState('');
  // 緊急連絡先と担当者。入力すれば印字し、空欄なら手書き用の罫線で刷る。
  // 現場ごと・当日ごとに変わるので必須にはしない。
  const [contactTel, setContactTel] = React.useState('');
  const [contactPerson, setContactPerson] = React.useState('');
  const [hydrated, setHydrated] = React.useState(false);

  React.useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw) as {
          orgName?: string;
          siteName?: string;
          contactTel?: string;
          contactPerson?: string;
        };
        if (typeof saved.orgName === 'string') setOrgName(saved.orgName);
        if (typeof saved.siteName === 'string') setSiteName(saved.siteName);
        if (typeof saved.contactTel === 'string') setContactTel(saved.contactTel);
        if (typeof saved.contactPerson === 'string') setContactPerson(saved.contactPerson);
      } else if (defaultOrgName) {
        setOrgName(defaultOrgName);
      }
    } catch {
      /* localStorage が使えない環境（プライベートモード等）では既定値のまま進む */
    }
    setHydrated(true);
  }, [defaultOrgName]);

  React.useEffect(() => {
    if (!hydrated) return;
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ orgName, siteName, contactTel, contactPerson }),
      );
    } catch {
      /* 保存できなくても機能は成立する */
    }
  }, [hydrated, orgName, siteName, contactTel, contactPerson]);

  /* 紙面に渡す形。空文字は undefined に落として「未入力＝罫線」を1か所で決める。 */
  const contact = React.useMemo(
    () => ({ tel: contactTel.trim() || undefined, person: contactPerson.trim() || undefined }),
    [contactTel, contactPerson],
  );

  // ── 選択状態。URL（/posters/H1）と同期させる。
  const [selectedId, setSelectedId] = React.useState<string | null>(
    () => findPoster(posterIdFromPath(window.location.pathname))?.variant.id ?? null,
  );

  React.useEffect(() => {
    const onPop = () => {
      setSelectedId(findPoster(posterIdFromPath(window.location.pathname))?.variant.id ?? null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const selected = findPoster(selectedId);

  const [themeKey, setThemeKey] = React.useState<PosterThemeKey>(
    () => findPoster(posterIdFromPath(window.location.pathname))?.theme.key ?? initialThemeKey ?? 'heat',
  );

  const openPoster = React.useCallback((theme: PosterTheme, variant: PosterVariant) => {
    setThemeKey(theme.key);
    setSelectedId(variant.id);
    window.history.pushState({ view: 'posters', posterId: variant.id }, '', `/posters/${variant.id}`);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const backToGallery = React.useCallback(() => {
    setSelectedId(null);
    window.history.pushState({ view: 'posters' }, '', '/posters');
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  // ── QR。実トークンと見本を型で分けて、死んだQRを掲示させない。
  const activeQrPoints = React.useMemo(() => qrPoints.filter((q) => q.isActive), [qrPoints]);
  const [qrPointId, setQrPointId] = React.useState<string>(initialQrPointId ?? '');
  const chosenQrPoint = activeQrPoints.find((q) => q.id === qrPointId) ?? activeQrPoints[0] ?? null;

  /* QRポイントは非同期で届く（ログイン時に App が読み込む）ので、
     届いた時点で選び直す。initialQrPointId が有効ならそれを優先し、
     無効化・削除済みなら先頭に落とす（死んだQRを紙面に埋めない）。 */
  React.useEffect(() => {
    if (activeQrPoints.length === 0) return;
    if (activeQrPoints.some((q) => q.id === qrPointId)) return;
    const wanted = initialQrPointId
      ? activeQrPoints.find((q) => q.id === initialQrPointId)
      : undefined;
    setQrPointId(wanted?.id ?? activeQrPoints[0].id);
  }, [activeQrPoints, initialQrPointId, qrPointId]);

  const origin = typeof window === 'undefined' ? 'https://anzenpost.com' : window.location.origin;
  const host = typeof window === 'undefined' ? 'anzenpost.com' : window.location.host;

  const qr: PosterQr = React.useMemo(() => {
    if (isSignedIn && chosenQrPoint) {
      return {
        kind: 'live',
        url: `${origin}/report/${chosenQrPoint.token}`,
        caption: `${host}/report/${chosenQrPoint.token}`,
      };
    }
    return { kind: 'sample', url: `${origin}/?ref=poster-sample` };
  }, [chosenQrPoint, host, isSignedIn, origin]);

  const theme = POSTER_THEMES.find((t) => t.key === themeKey) ?? POSTER_THEMES[0];

  // ── サムネイルの段組み。CSS の auto-fill と同じ計算を自前でやり、
  //    セル幅から紙面の縮小率を決める（1画面に1つの ResizeObserver で済む）。
  //    スマホは1列にすると1枚が画面いっぱい（≒500px）になり、7枚を見比べるのに
  //    延々スクロールさせることになる。狭い画面では2列に落として一覧性を優先する。
  const { ref: gridRef, width: gridWidth } = useElementWidth();
  const isNarrow = gridWidth > 0 && gridWidth < 520;
  const gap = isNarrow ? 12 : THUMB_GAP;
  const columns = isNarrow
    ? 2
    : Math.max(1, Math.floor((gridWidth + gap) / (THUMB_MIN_WIDTH + gap)));
  const cellWidth = gridWidth > 0 ? (gridWidth - gap * (columns - 1)) / columns : THUMB_MIN_WIDTH;
  const thumbScale = cellWidth / A4_WIDTH_PX;

  // ── プレビューの縮小率。
  const { ref: stageRef, scale: previewScale } = useFitScale(1);

  const handlePrint = React.useCallback(() => {
    /* 計測は印刷の妨げにならないよう先に投げっぱなしにする（await しない）。
       送るのはテーマ・バリアントID・実QR/見本QRの3つだけで、
       掲示者が入力した会社名・現場名・緊急連絡先・担当者は送らない。 */
    if (selected) {
      void logPosterPrint({
        themeKey: selected.theme.key,
        variantId: selected.variant.id,
        qrKind: qr.kind,
      });
    }
    window.print();
  }, [selected, qr.kind]);

  // ══════════════════════════════════════════════════════════
  // プレビュー（会社名を入れて刷る画面）
  // ══════════════════════════════════════════════════════════
  if (selected) {
    const { theme: t, variant: v } = selected;

    return (
      <div className="ap-print-reset min-h-screen bg-slate-100">
        <PosterSheetStyles />

        <div className="ap-no-print border-b border-slate-200 bg-white">
          <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-3 sm:px-6">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <button
                  onClick={backToGallery}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
                >
                  <ArrowLeft size={16} /> ポスター一覧
                </button>
                {/* 何を編集中なのかを常に見えるところに置く */}
                <h1 className="truncate text-base font-extrabold text-slate-900 sm:text-lg">
                  {t.label}のポスター
                  <span className="ml-2 hidden text-xs font-bold text-slate-400 sm:inline">{v.use}</span>
                </h1>
              </div>
              <span className="shrink-0 text-xs font-bold tracking-widest text-slate-400">{v.id}</span>
            </div>
            <StepRail current={2} />
          </div>
        </div>

        <div className="ap-print-reset mx-auto max-w-6xl px-4 py-6 sm:px-6">
          {/* 列は minmax(0,1fr)。auto にすると紙面（等倍794px）が列幅を押し広げ、
              useFitScale が広がった幅を測って縮小率が 1 に張り付く（モバイルで横スクロールする）。 */}
          <div className="ap-print-reset grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
            {/* 紙面 */}
            <div className="ap-print-reset order-1 min-w-0 overflow-hidden">
              <div className="ap-no-print mb-2 flex items-center gap-2 text-xs font-bold text-slate-500">
                <span className="rounded bg-slate-200 px-1.5 py-0.5 text-slate-700">A4 縦</span>
                実際に印刷される仕上がりです
              </div>
              <div ref={stageRef} className="ap-print-reset">
                <div className="ap-print-reset mx-auto shadow-xl shadow-slate-900/15" style={{ width: 'fit-content' }}>
                  <PosterScaler scale={previewScale}>
                    <PosterSheet
                      theme={t}
                      variant={v}
                      orgName={orgName}
                      siteName={siteName}
                      contact={contact}
                      qr={qr}
                    />
                  </PosterScaler>
                </div>
              </div>
            </div>

            {/* 操作パネル */}
            <aside className="ap-no-print order-2 space-y-4 lg:sticky lg:top-4 lg:self-start">
              <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <h2 className="mb-1 flex items-center gap-2 text-sm font-extrabold text-slate-900">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-safety-500 text-[11px] text-white">
                    2
                  </span>
                  掲示者を入れる
                </h2>
                <p className="mb-3 text-xs leading-relaxed text-slate-500">
                  入力するとすぐ左（スマホでは上）の紙面に反映されます。空欄のままにすると、手書き用の記入欄で刷られます。
                </p>

                <label className="mb-3 block">
                  <span className="mb-1 flex items-center gap-1.5 text-xs font-bold text-slate-700">
                    <Building2 size={13} /> 会社名
                  </span>
                  <input
                    type="text"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    maxLength={40}
                    placeholder="例：○○建設株式会社"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base text-slate-900 outline-none transition focus:border-safety-500 focus:ring-2 focus:ring-safety-500/20"
                  />
                </label>

                <label className="block">
                  <span className="mb-1 flex items-center gap-1.5 text-xs font-bold text-slate-700">
                    <MapPin size={13} /> 現場名・作業所名（任意）
                  </span>
                  <input
                    type="text"
                    value={siteName}
                    onChange={(e) => setSiteName(e.target.value)}
                    maxLength={40}
                    placeholder="例：△△ビル新築工事作業所"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base text-slate-900 outline-none transition focus:border-safety-500 focus:ring-2 focus:ring-safety-500/20"
                  />
                </label>

                {/* 緊急連絡先。倒れた人の通報は匿名QRではなく電話で受ける前提なので、
                    紙面のこの欄が「連絡先を周知した掲示物」の実体になる。 */}
                <div className="mt-3 border-t border-dashed border-slate-200 pt-3">
                  <label className="mb-3 block">
                    <span className="mb-1 flex items-center gap-1.5 text-xs font-bold text-slate-700">
                      <Phone size={13} /> 緊急連絡先（任意）
                    </span>
                    <input
                      type="tel"
                      value={contactTel}
                      onChange={(e) => setContactTel(e.target.value)}
                      maxLength={24}
                      placeholder="例：090-1234-5678"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base text-slate-900 outline-none transition focus:border-safety-500 focus:ring-2 focus:ring-safety-500/20"
                    />
                  </label>

                  <label className="block">
                    <span className="mb-1 flex items-center gap-1.5 text-xs font-bold text-slate-700">
                      <UserRound size={13} /> 担当者（任意）
                    </span>
                    <input
                      type="text"
                      value={contactPerson}
                      onChange={(e) => setContactPerson(e.target.value)}
                      maxLength={8}
                      placeholder="例：山田"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base text-slate-900 outline-none transition focus:border-safety-500 focus:ring-2 focus:ring-safety-500/20"
                    />
                  </label>

                  <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                    現場や日によって変わるなら、空欄のまま刷って現場で手書きするのが確実です。
                  </p>
                </div>
              </section>

              {/* QR */}
              <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <h2 className="mb-1 flex items-center gap-2 text-sm font-extrabold text-slate-900">
                  <QrCode size={16} className="text-safety-600" />
                  報告用QRコード
                </h2>

                {!isSignedIn ? (
                  <>
                    <p className="mb-3 text-xs leading-relaxed text-slate-500">
                      いまは<b className="text-slate-800">見本のQR</b>が入っています。読み取っても報告はできないため、紙面には「見本」と印字されます。
                    </p>
                    <button
                      onClick={onSignup}
                      className="flex w-full items-center justify-center gap-2 rounded-lg bg-industrial-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800"
                    >
                      無料登録して本物のQRを発行
                      <ChevronRight size={16} />
                    </button>
                    <p className="mt-2 text-[11px] leading-relaxed text-slate-400">
                      登録すると <span className="font-mono">{host}/report/…</span> の実URLが埋め込まれ、現場のスマホから匿名で報告できるようになります。
                    </p>
                  </>
                ) : activeQrPoints.length === 0 ? (
                  <>
                    <p className="mb-3 text-xs leading-relaxed text-slate-500">
                      有効なQRポイントがまだありません。先に1つ作ると、このポスターに実URLを埋め込めます。
                    </p>
                    <button
                      onClick={onManageQr}
                      className="flex w-full items-center justify-center gap-2 rounded-lg bg-industrial-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800"
                    >
                      <Plus size={16} /> QRポイントを作る
                    </button>
                  </>
                ) : (
                  <>
                    <label className="block">
                      <span className="mb-1 block text-xs font-bold text-slate-700">貼る場所（QRポイント）</span>
                      <select
                        value={chosenQrPoint?.id ?? ''}
                        onChange={(e) => setQrPointId(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-base text-slate-900 outline-none focus:border-safety-500"
                      >
                        {activeQrPoints.map((q) => (
                          <option key={q.id} value={q.id}>
                            {q.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-emerald-50 p-2 text-[11px] leading-relaxed text-emerald-800">
                      <Check size={13} className="mt-0.5 shrink-0" />
                      <span>
                        実際に報告できるQRです。掲示前に一度スマホで読み取って確認してください。
                      </span>
                    </p>
                  </>
                )}
              </section>

              {/* 印刷 */}
              <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <h2 className="mb-3 flex items-center gap-2 text-sm font-extrabold text-slate-900">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-safety-500 text-[11px] text-white">
                    3
                  </span>
                  印刷する
                </h2>

                {!orgName.trim() && (
                  <p className="mb-3 flex items-start gap-1.5 rounded-lg bg-amber-50 p-2 text-[11px] leading-relaxed text-amber-900">
                    <TriangleAlert size={13} className="mt-0.5 shrink-0" />
                    <span>会社名が空欄です。このまま刷ると手書き用の記入欄になります。</span>
                  </p>
                )}

                <button
                  onClick={handlePrint}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-safety-500 px-4 py-4 text-base font-extrabold text-white shadow-lg shadow-safety-500/30 transition hover:bg-safety-600"
                >
                  <Printer size={18} /> このポスターを印刷
                </button>

                <div className="mt-3 rounded-lg bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-600">
                  <p className="mb-1 flex items-center gap-1 font-bold text-slate-700">
                    <Info size={12} /> 印刷ダイアログでの設定
                  </p>
                  <ul className="space-y-0.5">
                    <li>・用紙サイズ：<b>A4</b>（A3を選べば拡大掲示もできます）</li>
                    <li>・余白：<b>なし</b></li>
                    <li>・「背景のグラフィック」を<b>オン</b></li>
                  </ul>
                </div>
              </section>

              <button
                onClick={backToGallery}
                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-600 transition hover:bg-slate-50"
              >
                ほかのデザインを見る（全{POSTER_TOTAL}種）
              </button>
            </aside>
          </div>
        </div>

        {/* モバイル：親指の届く位置に印刷ボタンを常駐させる */}
        <div className="ap-no-print sticky bottom-0 z-30 border-t border-slate-200 bg-white/95 p-3 backdrop-blur lg:hidden">
          <button
            onClick={handlePrint}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-safety-500 px-4 py-4 text-base font-extrabold text-white shadow-lg shadow-safety-500/30"
          >
            <Printer size={18} /> このポスターを印刷（A4）
          </button>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // 一覧
  // ══════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-slate-100">
      <PosterSheetStyles />

      {/* 見出し。売り込みの壁は作らず、何ができて何が要るかだけを言う。 */}
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
          {onGoHome && (
            <button
              onClick={onGoHome}
              className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition hover:text-slate-900"
            >
              <ArrowLeft size={15} /> {homeLabel}
            </button>
          )}
          {/* ログイン中に「登録なしで印刷できます」は事実と食い違う。
              紙面に実トークンが入るかどうかまで見て3状態で出し分ける
              （登録済みでも有効なQRポイントが無ければ見本のままなので言い切らない）。 */}
          <p className="mb-2 text-xs font-extrabold uppercase tracking-[0.18em] text-safety-600">
            {!isSignedIn
              ? '無料・登録なしで印刷できます'
              : qr.kind === 'live'
                ? '自社の報告用QRを入れて印刷できます'
                : 'QRポイントを作ると自社の報告用QRが入ります'}
          </p>
          <h1 className="text-3xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-4xl">
            現場に貼れる、安全掲示ポスター
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
            熱中症・墜落・はさまれ・転倒＋業種を問わない汎用の5テーマ、全{POSTER_TOTAL}種。会社名と現場名を入れて、A4で印刷するだけです。
            緊急連絡先は入力しても、空欄で刷って現場で手書きしてもかまいません。災害別の4テーマには根拠となる法令も載せています。
          </p>
          <div className="mt-5">
            <StepRail current={1} />
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        {/* テーマ選択。細いタブではなく、指で押せる大きさのカードにする。 */}
        <h2 className="mb-3 flex items-center gap-2 text-sm font-extrabold text-slate-900">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-safety-500 text-[11px] text-white">
            1
          </span>
          防ぎたい災害を選ぶ
        </h2>
        <div className="mb-8 grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3 lg:grid-cols-5">
          {POSTER_THEMES.map((t) => {
            const active = t.key === themeKey;
            return (
              <button
                key={t.key}
                onClick={() => setThemeKey(t.key)}
                aria-pressed={active}
                className={`rounded-xl border-2 px-3 py-3 text-left transition ${
                  active
                    ? 'border-safety-500 bg-safety-50 shadow-md shadow-safety-500/10'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <div className="flex items-baseline justify-between gap-1">
                  <span
                    className={`text-sm font-extrabold leading-tight sm:text-base ${
                      active ? 'text-safety-700' : 'text-slate-900'
                    }`}
                  >
                    {t.label}
                  </span>
                  <span
                    className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                      active ? 'bg-safety-500 text-white' : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {t.variants.length}種
                  </span>
                </div>
                <span className="mt-1 block text-[11px] leading-snug text-slate-500">{t.tagline}</span>
              </button>
            );
          })}
        </div>

        {/* 一覧 */}
        <div className="mb-3 flex items-baseline gap-2">
          <h2 className="text-lg font-extrabold text-slate-900">{theme.label}のポスター</h2>
          <span className="text-xs text-slate-500">{theme.variants.length}種類</span>
        </div>

        <div
          ref={gridRef}
          className="grid"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap }}
        >
          {theme.variants.map((v) => (
            /* button 要素の中に紙面（div/svg/ul）は入れられないので、
               role="button" + キーボード操作で同等の当たり判定を作る。 */
            <div
              key={v.id}
              role="button"
              tabIndex={0}
              aria-label={`${theme.label}のポスター ${v.id}：${v.s1}`}
              onClick={() => openPoster(theme, v)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openPoster(theme, v);
                }
              }}
              className="group flex cursor-pointer flex-col overflow-hidden rounded-xl border border-slate-200 bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:border-safety-400 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-safety-500"
            >
              {/* 紙面のミニチュア。実際の組版そのままを縮小しているので、
                  仕上がりと見た目がずれない。文字は読めないので下に実テキストを併記する。 */}
              <div
                className="overflow-hidden bg-slate-200"
                style={{ width: '100%', height: A4_HEIGHT_PX * thumbScale }}
                aria-hidden="true"
              >
                <PosterScaler scale={thumbScale}>
                  <PosterSheet
                    theme={theme}
                    variant={v}
                    orgName={orgName}
                    siteName={siteName}
                    contact={contact}
                    qr={qr}
                    lazyImage
                  />
                </PosterScaler>
              </div>

              <div className="flex flex-1 flex-col gap-1.5 border-t border-slate-100 p-3">
                <div className="flex items-center gap-1.5">
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wider text-slate-500">
                    {v.id}
                  </span>
                  <span className="truncate text-[11px] font-bold text-slate-500">{v.use}</span>
                </div>
                <p className="text-[11px] leading-snug text-slate-500">{v.s1}</p>
                <p className="text-[15px] font-extrabold leading-snug text-slate-900">
                  {v.s2.map((line) => line.map((seg) => seg.t).join('')).join(' ')}
                </p>
                <span className="mt-auto flex items-center gap-1 pt-2 text-xs font-extrabold text-safety-600 group-hover:text-safety-700">
                  このポスターを使う <ChevronRight size={14} />
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* 未ログイン向けの案内は最後に置く。先に置くと選ぶ手が止まる。 */}
        {!isSignedIn && (
          <section className="mt-10 rounded-2xl border border-safety-200 bg-safety-50 p-5 sm:p-7">
            <h2 className="text-lg font-extrabold text-slate-900 sm:text-xl">
              QRを「本物」にすると、掲示が報告に変わります
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-700">
              いまダウンロードできる紙面には<b>見本のQR</b>が入っています。無料登録すると自社の報告用URLが発行され、
              ポスターのQRを読み取った現場の人が、その場で匿名のヒヤリハットを送れるようになります。
              集まった報告はAIが匿名化と4M分析をして、管理画面に並びます。
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                onClick={onSignup}
                className="inline-flex items-center gap-2 rounded-xl bg-industrial-900 px-5 py-3 text-sm font-extrabold text-white transition hover:bg-slate-800"
              >
                無料で登録してQRを発行 <ChevronRight size={16} />
              </button>
              {onGoHome && (
                <button
                  onClick={onGoHome}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-bold text-slate-800 transition hover:bg-slate-50"
                >
                  安全ポスト＋とは
                </button>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default PosterGalleryView;
